import { Stack, Typography } from '@mui/material';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import { ChevronRight } from '@mui/icons-material';
import { PHASE_TYPES } from '@vy/types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { prefetchGetPhaseLabels } from '../../../../../hooks/requests/use-phase-labels';
import React, { useEffect } from 'react';
import { withPrefetch } from '../../../../../components/hocs';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { NewPhaseForm } from './NewPhaseForm';
import { NewPhaseForm as NewPhaseFormType, createNewPhaseFormSchemaWithValidation } from '../types/new-phase-form-schema';
import { PLAN_CREATION_REASONS } from '../../../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { formatDate } from '../../../../../utils/date';
import { useContractPage } from '../../../../../hooks/requests/pages/use-contract-page';
import { useContract } from '../../../../../hooks/requests/use-contract';
import { isUnlimitedDuration } from '@vy/services';
import { last } from 'lodash';
import { whenDefined } from '@vy/null';

type CreatePhaseDialogProps = {
  isDialogOpen: boolean;
  handleClose: () => void;
  contractStartDate: Date;
  contractEndDate?: Date;
  contractId: string;
  dialogTitle?: string;
  dialogDescription?: string;
  nameInputLabel?: string;
  dateInputLabel?: string;
};

const CreatePhaseDialogContent: React.FC<CreatePhaseDialogProps> = ({
  isDialogOpen,
  handleClose,
  contractStartDate,
  contractEndDate,
  contractId,
  dialogTitle = 'Update and add phase to this contract',
  dialogDescription = 'Set up a new phase with new products, custom terms and timing',
  nameInputLabel,
  dateInputLabel,
}) => {
  const navigate = useVyNavigate();
  const { data: contractData } = useContractPage(contractId, { suspense: true });
  const {
    data: { contract },
  } = useContract(contractId, { suspense: true });

  const planDuration = contract.plan.billingData.duration;
  const coTermDisabled = isUnlimitedDuration(planDuration);

  const { versions } = contractData?.contractPageData ?? {};

  const lasPhaseStartDate = last(versions)?.effectiveAt;

  const minPhaseStartDate = getPhaseMinStartDate(
    lasPhaseStartDate,
    contractStartDate,
  );
  const maxPhaseStartDate = whenDefined(contractEndDate, vyDate);

  const schema = createNewPhaseFormSchemaWithValidation(
    vyDate(contractStartDate),
    whenDefined(contractEndDate, vyDate),
    whenDefined(lasPhaseStartDate, vyDate),
  );

  const { control, handleSubmit, watch, reset, setValue } = useForm<NewPhaseFormType>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(),
  });

  const effectiveDate = watch('effectiveDate');
  const formattedEffectiveDate = isNotNullOrUndefined(effectiveDate)
    ? formatDate(effectiveDate, 'MMMM DD, YY')
    : 'N/A';

  const submitHandler = handleSubmit((data) => {
    navigate(
      `/plans/contracts/${contractId}/revise?planCreateReason=${PLAN_CREATION_REASONS.AddPhaseToCurrentContract}`,
      {
        state: {
          ...data,
        },
      },
    );
  });

  useEffect(() => {
    if (isDialogOpen) {
      reset(getDefaultValues());
    }
  }, [isDialogOpen]);

  return (
    <VyDialog
      maxWidth="sm"
      open={isDialogOpen}
      onClose={handleClose}
      label={dialogTitle}
      labelDescription={dialogDescription}
      ctaButtonProps={{
        endIcon: <ChevronRight />,
      }}
      slotProps={{
        paper: {
          sx: {
            maxWidth: '558px',
            width: '100%',
          },
        },
      }}
      labelDescriptionProps={{ sx: { fontSize: '14px' } }}
      ctaText="Go to plan builder"
      onSubmit={submitHandler}
      cancelButtonText="Cancel"
    >
      <Stack
        sx={{
          flexDirection: "column",
          gap: 2
        }}>
        <Typography variant="body1">
          Contract start date: <b>{formatDate(contractStartDate, 'MMMM DD, YY')}</b>
        </Typography>

        <NewPhaseForm
          control={control}
          setValue={setValue}
          planDuration={planDuration}
          coTermDisabled={coTermDisabled}
          minDate={minPhaseStartDate}
          maxDate={maxPhaseStartDate}
          nameInputLabel={nameInputLabel}
          dateInputLabel={dateInputLabel}
        />

        {isNotNullOrUndefined(effectiveDate) && (
          <Typography variant="body1">
            The phase you created will take affect from <b>{formattedEffectiveDate}</b>.
          </Typography>
        )}
      </Stack>
    </VyDialog>
  );
};

function getDefaultValues() {
  return {
    effectiveDate: undefined,
    label: PHASE_TYPES.PriceUpdate,
    coTerm: true,
  };
}

const CreatePhaseDialogContentWithBoundary = withPrefetch(
  withSuspenseBoundary(CreatePhaseDialogContent, { suspenseProps: { fallback: null } }),
  prefetchGetPhaseLabels,
);

const CreatePhaseDialog: React.FC<CreatePhaseDialogProps> = (props) => {
  if (!props.isDialogOpen) {
    return null;
  }

  return <CreatePhaseDialogContentWithBoundary {...props} />;
};

export { CreatePhaseDialog };

function getPhaseMinStartDate(
  lastPhaseStartDate: Date | undefined,
  startDate: Date,
) {
  return vyDate(lastPhaseStartDate ?? startDate).add(1, 'day');
}
